import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import i18n, { chooseLanguage, normalizeLang } from "@/i18n";
import { getCurrency, setCurrency, normalizeCurrency } from "@/lib/currency";

/** Account metadata is preference-only, never authorization data. */
export function PreferenceSync() {
  const { user } = useAuth();
  const router = useRouter();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const changed = () => {
      const lang = normalizeLang(i18n.language);
      const match = window.location.pathname.match(/^\/(es|en|fr|de|it|pt)\/tour\/(.+)$/);
      if (match && match[1] !== lang) {
        const slug = decodeURIComponent(match[2]);
        if (lang === "es") void router.navigate({ to: "/tour/$slug", params: { slug }, search: true, replace: true });
        else void router.navigate({ to: "/$lang/tour/$slug", params: { lang, slug }, search: true, replace: true });
      }
      clearTimeout(timer);
      if (!user) return;
      timer = setTimeout(() => {
        void import("@/integrations/supabase/client").then(({ supabase }) =>
          supabase.auth.updateUser({ data: { preferred_language: lang, preferred_currency: getCurrency() } }),
        ).catch(() => null);
      }, 500);
    };
    window.addEventListener("mm:preferences-changed", changed);
    return () => { clearTimeout(timer); window.removeEventListener("mm:preferences-changed", changed); };
  }, [user?.id, router]);
  useEffect(() => {
    if (!user) return;
    // Existing local choices win; account preferences restore on a new device.
    try {
      if (!localStorage.getItem("mm.lang") && user.user_metadata.preferred_language) void chooseLanguage(normalizeLang(user.user_metadata.preferred_language));
      const saved = user.user_metadata.preferred_currency;
      if (!localStorage.getItem("mm.currency") && typeof saved === "string") setCurrency(normalizeCurrency(saved));
      const localLang = localStorage.getItem("mm.lang");
      const localCurrency = localStorage.getItem("mm.currency");
      if (localLang || localCurrency) {
        void import("@/integrations/supabase/client").then(({ supabase }) => supabase.auth.updateUser({ data: {
          ...(localLang ? { preferred_language: normalizeLang(localLang) } : {}),
          ...(localCurrency ? { preferred_currency: getCurrency() } : {}),
        } })).catch(() => null);
      }
    } catch { /* local preferences remain usable without storage */ }
  }, [user?.id]);
  return null;
}